<template>
  <div class="payment-splitter">
    <div class="ps-header">
      <span class="ps-title">
        <b-icon icon="credit-card-2-front"></b-icon>
        {{ $t("To'lov turlari") }}
      </span>
      <button type="button" class="ps-add-btn" @click="addRow">
        <b-icon icon="plus-circle"></b-icon> {{ $t("Qo'shish") }}
      </button>
    </div>

    <div v-if="rows.length === 0" class="ps-empty">
      {{ emptyText }}
    </div>

    <div v-for="(row, idx) in rows" :key="idx" class="ps-row" :class="{ 'ps-row-dollar': isDollar(row.pay_type_id) }">
      <div class="ps-select">
        <v-select
          :clearable="false"
          :options="availableTypesFor(idx)"
          v-model="row.pay_type_id"
          :reduce="t => t.id"
          label="name"
          :placeholder="$t('Tanlang')"
        >
          <template v-slot:no-options>
            <em style="opacity:.5">{{ $t("Bo'sh") }}</em>
          </template>
          <template #selected-option="{ name, id }">
            {{ name }}
            <span v-if="isDollar(id)" class="ps-currency-badge">$</span>
          </template>
        </v-select>
      </div>
      <div class="ps-summa">
        <summa-input
          v-model="row.summa"
          :placeholder="isDollar(row.pay_type_id) ? '$ ' + $t('Summa') : $t('Summa')"
        />
        <small v-if="isDollar(row.pay_type_id) && row.summa > 0 && dollarRate > 0" class="ps-row-hint">
          ≈ {{ formatMoney(row.summa * dollarRate) }} so'm
        </small>
      </div>
      <button
        type="button"
        class="ps-del-btn"
        @click="removeRow(idx)"
      >
        <b-icon icon="x-circle"></b-icon>
      </button>
    </div>

    <div v-if="rows.length > 0" class="ps-footer">
      <div class="ps-totals">
        <div class="ps-total">
          <span class="ps-label">{{ $t("Jami (so'm)") }}:</span>
          <strong>{{ formatMoney(totalEnteredSom) }}</strong>
          <span v-if="totalDollar > 0" class="ps-dollar-info">
            ({{ $t("shu jumladan") }} ${{ formatMoney(totalDollar) }} × {{ dollarRate }})
          </span>
        </div>
        <div v-if="expectedTotal !== null && expectedTotal !== undefined && Number(expectedTotal) > 0" class="ps-expected" :class="diffClass">
          <span class="ps-label">{{ $t("Kutilgan") }}:</span>
          <strong>{{ formatMoney(expectedTotal) }}</strong>
          <span v-if="Math.abs(diff) > 0.001" class="ps-diff">
            ({{ diff > 0 ? '+' : '' }}{{ formatMoney(diff) }} {{ diff > 0 ? $t("ortiqcha") : $t("nasiya") }})
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentSplitter',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    paytypes: {
      type: Array,
      required: true
    },
    expectedTotal: {
      type: [Number, String],
      default: null
    },
    autofillFirst: {
      type: Boolean,
      default: true
    },
    // Qaysi pay_type_id lar dollar valyutasida (sum'ni dollar kursiga ko'paytirish kerak)
    dollarPayTypeIds: {
      type: Array,
      default: () => [3]
    },
    // Dollar kursi (1$ = X so'm)
    dollarRate: {
      type: [Number, String],
      default: 1
    }
  },
  data() {
    return {
      rows: []
    };
  },
  computed: {
    // So'm ekvivalentidagi jami (dollar entrylar kurs bo'yicha aylantiriladi)
    totalEnteredSom() {
      const rate = parseFloat(this.dollarRate) || 1;
      return this.rows.reduce((acc, r) => {
        const s = parseFloat(r.summa) || 0;
        if (s <= 0) return acc;
        if (this.isDollar(r.pay_type_id)) return acc + s * rate;
        return acc + s;
      }, 0);
    },
    // Faqat dollar entrylar yig'indisi ($ da)
    totalDollar() {
      return this.rows.reduce((acc, r) => {
        const s = parseFloat(r.summa) || 0;
        if (this.isDollar(r.pay_type_id)) return acc + s;
        return acc;
      }, 0);
    },
    diff() {
      const exp = parseFloat(this.expectedTotal) || 0;
      return this.totalEnteredSom - exp;
    },
    diffClass() {
      if (Math.abs(this.diff) < 0.001) return 'ps-ok';
      if (this.diff > 0) return 'ps-over';
      return 'ps-under';
    },
    emptyText() {
      return this.$t("To'lov turi qo'shilmagan. Qo'shish tugmasini bosing.");
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (!Array.isArray(val) || val.length === 0) {
          if (this.rows.length !== 0) this.rows = [];
          return;
        }
        // Tashqaridan kelganda — ichki holatni yangilash (faqat farq bo'lsa)
        const sameLen = this.rows.length === val.length;
        let identical = sameLen;
        if (sameLen) {
          for (let i = 0; i < val.length; i++) {
            if (
              this.rows[i].pay_type_id !== val[i].pay_type_id ||
              parseFloat(this.rows[i].summa || 0) !== parseFloat(val[i].summa || 0)
            ) { identical = false; break; }
          }
        }
        if (!identical) {
          this.rows = val.map(v => ({
            pay_type_id: v.pay_type_id != null ? parseInt(v.pay_type_id) : null,
            summa: parseFloat(v.summa) || 0
          }));
        }
      }
    },
    rows: {
      deep: true,
      handler() { this.emitChange(); }
    }
  },
  methods: {
    formatMoney(v) {
      const num = parseFloat(v) || 0;
      return num.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    isDollar(payTypeId) {
      if (payTypeId == null) return false;
      return (this.dollarPayTypeIds || []).indexOf(parseInt(payTypeId)) !== -1;
    },
    availableTypesFor(currentIdx) {
      const usedIds = this.rows
        .map((r, i) => (i === currentIdx ? null : r.pay_type_id))
        .filter(id => id != null);
      return (this.paytypes || []).filter(t => usedIds.indexOf(t.id) === -1);
    },
    addRow() {
      // Bo'sh paytype tanlash — birinchi mavjudni avtomatik tanlash
      const used = this.rows.map(r => r.pay_type_id).filter(x => x != null);
      const free = (this.paytypes || []).find(t => used.indexOf(t.id) === -1);
      let initSumma = 0;
      if (this.autofillFirst && this.rows.length === 0 && this.expectedTotal) {
        initSumma = parseFloat(this.expectedTotal) || 0;
      }
      this.rows.push({
        pay_type_id: free ? free.id : null,
        summa: initSumma
      });
    },
    removeRow(idx) {
      this.rows.splice(idx, 1);
    },
    emitChange() {
      // Faqat to'liq qatorlarni emit qilish
      const clean = this.rows
        .filter(r => r.pay_type_id != null && parseFloat(r.summa) > 0)
        .map(r => ({ pay_type_id: parseInt(r.pay_type_id), summa: parseFloat(r.summa) }));
      const current = Array.isArray(this.value) ? this.value : [];
      const identical = clean.length === current.length && clean.every((row, index) => (
        row.pay_type_id === parseInt(current[index].pay_type_id)
        && row.summa === (parseFloat(current[index].summa) || 0)
      ));
      if (!identical) this.$emit('input', clean);
    }
  }
};
</script>

<style scoped>
.payment-splitter {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px;
}

.ps-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.ps-title {
  font-weight: 700;
  font-size: 13px;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ps-add-btn {
  background: #a142f4;
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ps-add-btn:hover {
  background: #8e39d9;
}

.ps-empty {
  text-align: center;
  color: #94a3b8;
  font-style: italic;
  font-size: 13px;
  padding: 14px 8px;
}

.ps-row {
  display: grid;
  grid-template-columns: minmax(140px, 1fr) minmax(120px, 1fr) 36px;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.ps-row-dollar {
  background: linear-gradient(90deg, #ecfdf5, transparent);
  border-radius: 10px;
  padding: 4px 4px;
}

.ps-currency-badge {
  display: inline-block;
  background: #10b981;
  color: white;
  font-size: 10px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 8px;
  margin-left: 6px;
}

.ps-row-hint {
  display: block;
  font-size: 10px;
  color: #16a34a;
  font-weight: 600;
  margin-top: 2px;
  text-align: right;
}

.ps-dollar-info {
  display: block;
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
}

.ps-select ::v-deep .vs__dropdown-toggle {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 4px;
  min-height: 36px;
}

.ps-summa input {
  width: 100%;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 600;
  color: #2c3e50;
  outline: none;
  text-align: right;
}

.ps-summa input:focus {
  border-color: #a142f4;
  box-shadow: 0 0 0 3px rgba(161, 66, 244, 0.08);
}

.ps-del-btn {
  background: #fff1f2;
  color: #e11d48;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ps-del-btn:hover {
  background: #e11d48;
  color: white;
}

.ps-footer {
  border-top: 1px dashed #e2e8f0;
  padding-top: 10px;
  margin-top: 6px;
}

.ps-totals {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.ps-total strong, .ps-expected strong {
  font-size: 14px;
  margin-left: 6px;
}

.ps-label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.ps-expected.ps-ok strong { color: #16a34a; }
.ps-expected.ps-over strong { color: #ea580c; }
.ps-expected.ps-under strong { color: #e11d48; }

.ps-diff {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 600;
}
.ps-ok .ps-diff { color: #16a34a; }
.ps-over .ps-diff { color: #ea580c; }
.ps-under .ps-diff { color: #e11d48; }
</style>
